import { useCallback, useContext } from "react";
import { WebsiteContext } from "@/pages/website/WebsiteProvider";
import { ICheckout } from "@/pages/checkout/hooks/useCheckoutSteps";
import { useUpdateOrderMutation } from "@/features/organizations/api/ordersApi";
import { Order, OrderStatus } from "@/types";
import { useAppSelector } from "@/hooks";
import pick from "lodash/pick";
import isEmpty from "lodash/isEmpty";
import { contactFormFields } from "@/pages/checkout/components/ContactForm";
import { locationFormFields } from "@/pages/checkout/components/LocationForm";
import useSendThroughWhatsapp from "@/pages/checkout/hooks/useSendThroughWhatsapp";
import { scheduleTimeFormFields } from "../components/ScheduleTimeForm";
import useRoutes from "@/hooks/useRoutes";
import { useParams } from "react-router-dom";
import { ORDER_SOURCES } from "@/constants/global";
import { useCustomer } from "@/hooks/useCustomer";
import { calculateOrderTotals } from "@/utils/order";
import useServiceFee from "@/hooks/useServiceFee";

const useSendOrder = () => {
  const [navigate] = useRoutes();
  const { orderId } = useParams();
  const [updateOrder] = useUpdateOrderMutation();
  const { organization, branch: websiteBranches } = useContext(WebsiteContext);
  const activeOrder = useAppSelector((store) => store.order);
  const sendThroughWhatsapp = useSendThroughWhatsapp();
  const { organizationAlias } = useParams<{ organizationAlias: string }>();
  const { getOrCreateCustomer, addOrderToCustomer } = useCustomer({
    organizationId: organization?.id ?? "",
  });
  const service = useServiceFee();
  const activeBranch = useAppSelector((state) => state.ui.activeBranch);


  const sendOrder = useCallback(
    async (values: ICheckout) => {
      if (!organization) return;

      let order: Order = { ...activeOrder };

      // payment details
      if (values.paymentDetails) {
        order = {
          ...order,
          paymentDetails: {
            method: values.paymentDetails.method,
            value: values.paymentDetails.value,
          },
        };
      }

      // contact details
      const contactDetails = pick(values, contactFormFields) as Order["client"];

      if (!isEmpty(contactDetails)) {
        order = {
          ...order,
          client: contactDetails,
        };
      }

      // obtener o crear cliente y asociar orden
      const formLocationFields = pick(values, locationFormFields) as Order["deliveryLocation"];
      const completeLocation = {
        ...formLocationFields,
        ...(order?.deliveryLocation || {}),
      }

      if (contactDetails?.phone && order?.id) {

        try {
          // if the customer exists, get it; if not create it and in both cases associate the order
          const customer = await getOrCreateCustomer(
            contactDetails.phone,
            {
              name: contactDetails.name,
              lastname: contactDetails.lastname,
              email: contactDetails.email,
              phone: contactDetails.phone,
              locations: completeLocation ? [completeLocation] : [],
            }
          );
          if (!customer) {
            throw new Error("No se pudo obtener ni crear cliente");
          }

          // Inyectar el ID real del cliente a la orden para que el sistema de lealtad lo reconozca
          if (order.client) {
            order.client = {
              ...order.client,
              id: customer.id,
            };
          }

          const { subtotal, total } = calculateOrderTotals(order.items, order.tipPercentage, order.areTipsIncluded);

          await addOrderToCustomer({
            userId: customer.id,
            orderId: order.id,
            subtotal,
            total
          });
        } catch (error) {

          console.error("Error asociando cliente u orden", error);
        }
      }

      // location location
      if (!isEmpty(completeLocation)) {
        const draftOrder = {
          ...order,
          deliveryLocation: completeLocation,
        };

        if (order.flow === "delivery" && service.fee) {
          draftOrder.deliveryFee = service.fee;
        }

        order = draftOrder;
      }

      // branch details
      let branchToAssign = values?.branch || order?.branch;
      if (!branchToAssign?.id) {
        const branchList = (websiteBranches && websiteBranches.length > 0) ? websiteBranches : (organization?.branches || []);
        const targetBranchId = activeBranch || localStorage.getItem("branchId") || organization?.mainBranch;
        if (targetBranchId && branchList.length > 0) {
          const found = branchList.find((b: any) => b.id === targetBranchId);
          if (found) {
            branchToAssign = pick(found, ["id", "title", "location", "whatsapp"]);
          }
        } else if (branchList.length === 1) {
          branchToAssign = pick(branchList[0], ["id", "title", "location", "whatsapp"]);
        }
      }

      if (branchToAssign) {
        order = {
          ...order,
          branch: pick(branchToAssign, ["id", "title", "location", "whatsapp"]),
        };
      }

      // schedule time
      const scheduleTime = pick(values, scheduleTimeFormFields) as Pick<Order, "scheduleTime">;
      if (!isEmpty(scheduleTime)) {
        order = {
          ...order,
          ...scheduleTime,
        };
      }

      //
      if (!order.items?.length) {
        console.error("Error al enviar orden, no hay items", order);
        return;
      }
      //

      // check org preferences
      if (organization.settings?.orderManagement === "whatsapp") {
        await sendThroughWhatsapp({
          ...order,
          status: OrderStatus.REVIEWING,
        });
      } else if (orderId) {
        await updateOrder({
          organizationId: organization.id,
          order: {
            ...order,
            id: orderId,
            status: OrderStatus.REVIEWING,
            source: ORDER_SOURCES.BITELINK,
          },
        });
      }

      if (organizationAlias) {
        navigate("organizations.order.status", { orderId: order.id });
      } else {
        navigate("orderStatus", { orderId: order.id });
      }

    },
    [
      orderId,
      organization,
      activeOrder,
      activeBranch,
      service.fee,
      organizationAlias,
      navigate,
      sendThroughWhatsapp,
      updateOrder,
    ]
  );

  return sendOrder;
};

export default useSendOrder;
